import type { Product, Persona } from "@shared/schema";

export interface NarrativeOpportunity {
  id: string;
  generationId: string;
  title: string;
  opportunity_type: string;
  core_tension: string;
  persona_reason: string;
  product_anchor: string;
  why_promising: string;
  strength: number;
}

export interface NarrativeAngle {
  id: string;
  generationId: string;
  name: string;
  family: string | null;
  subtype: string | null;
  summary: string | null;
  coreTension: string | null;
  personaRelevance: string | null;
  productTruth: string | null;
  beliefShift: string | null;
  promise: string | null;
  resonanceScore: number | null;
  proofScore: number | null;
  distinctivenessScore: number | null;
  emotionalScore: number | null;
  status: string;
  isFavorite: boolean | null;
  /** Legacy campaign-wizard responses used this to carry the source opportunity title. */
  notes?: string | null;
}

export interface FunnelStrategySummary {
  core_tension: string;
  desired_transformation: string;
  primary_promise: string;
  main_conversion_friction: string;
  best_proof_types: string[];
  brand_role: string;
}

export interface FunnelStageStrategy {
  /** Legacy aliases retained by saved campaign drafts. */
  id?: string;
  name?: string;
  stage: string;
  stage_group: string;
  objective: string;
  psychological_task: string;
  messaging_focus: string;
  emotional_job: string;
  proof_focus: string;
  recommended_angle_ids: string[];
  message_style: string;
  what_not_to_say_yet: string;
  transition_to_next_stage: string;
}

export interface FunnelStrategyData {
  strategy_summary: FunnelStrategySummary;
  stages: FunnelStageStrategy[];
  /** Legacy serialized key retained for backwards-compatible draft reads. */
  funnel_stages?: FunnelStageStrategy[];
  generatedAt: string;
}

export interface MediaMixConfig {
  totalUnits: number;
  staticUnits: number;
  videoUnits: number;
}

export interface WizardAdUnit {
  id: string;
  /** Present on legacy ad-unit payloads; newer catalog rows use `id`. */
  slug?: string;
  name: string;
  format: "static" | "video";
  category: string;
  description: string;
  supported_funnel_stages: string[];
  recommended_use_case: string;
  exampleImages?: string[];
}

export interface AdUnitSelection {
  adUnitId: string;
  adUnit: WizardAdUnit;
  funnelStageId: string;
  quantity: number;
  /**
   * URL of the specific example image the user picked as the visual reference
   * for this ad unit at this funnel stage. Used as the base image for
   * downstream creative-brief generation and image generation. Defaults to
   * the first `exampleImages` entry when the unit is added.
   */
  selectedImageUrl?: string | null;
}

export interface PlannedAd {
  id: string;
  ad_unit_id: string;
  ad_unit_name: string;
  format: "static" | "video";
  angle_id: string;
  angle_name: string;
  headline_direction: string;
  hook_direction: string;
  creative_structure: string;
  copy_direction: string;
  recommended: boolean;
  selected: boolean;
}

export interface AdUnitStagePlan {
  stage: string;
  stageRole: string;
  targetAssetCount: number;
  rationale: string;
  recommendedFormats: ("static" | "video")[];
  assignedAngleIds: string[];
  diversityNote: string;
  ads: PlannedAd[];
}

export interface AdUnitPlanSummary {
  total_assets: number;
  static_assets: number;
  video_assets: number;
  stages_used: number;
  strategy_summary: string;
  diversity_summary: string;
}

export interface AdUnitPlan {
  status: "empty" | "planned" | "stale";
  generatedAt: string | null;
  summary: AdUnitPlanSummary | null;
  stagePlans: AdUnitStagePlan[];
  error: string | null;
}

export interface CampaignPlanStage {
  stageId: string;
  stageName: string;
  adUnits: AdUnitSelection[];
  budgetPercent: number;
  goal: string;
  primaryMessage: string;
}

export interface CampaignPlanStageStrategy {
  stage: string;
  pitch: string;
  psychologicalGoal: string;
  messagingStrategy: string;
  copyDirections: string[];
  transitionToNextStage: string;
  assetCount?: number;
}

export interface CampaignFunnelOverviewItem {
  stage: string;
  role: string;
  assetCount?: number;
}

export interface CampaignPlanSummary {
  id: string;
  name: string;
  stages: CampaignPlanStage[];
  totalAdUnits: number;
  status: "draft" | "generated" | "approved" | "needs_revision" | "stale";
  executiveSummary?: string;
  keyTakeaways?: string[];
  funnelOverview?: CampaignFunnelOverviewItem[];
  stageStrategy?: CampaignPlanStageStrategy[];
  whyThisCampaignWins?: string[];
  compiledAt?: string;
  error?: string | null;
}

export interface CreativeBriefResult {
  objective: string;
  hook_direction: string;
  headline_options: string[];
  copy_direction: string;
  visual_direction: string;
  structure_template: string[];
  proof_elements: string[];
  cta_direction: string;
  designer_notes: string;
  ai_generation_prompt: string;
}

export interface BriefJob {
  jobId: string;
  stage: string;
  stageOrder: number;
  adUnitId: string;
  adUnitName: string;
  adUnit: WizardAdUnit;
  funnelStageId: string;
  format: "static" | "video";
  instanceIndex: number;
  angleId: string | null;
  angleName: string | null;
  /** User-selected reference image (if any) carried through from the Ad Unit Selection step. */
  referenceImageUrl: string | null;
  status: "pending" | "running" | "completed" | "failed" | "locked";
  brief: CreativeBriefResult | null;
  error: string | null;
  generatedImageUrl: string | null;
  imageStatus: "idle" | "generating" | "completed" | "failed";
  imageError: string | null;
}

export interface StageImageJob {
  id: string;
  adUnitName: string;
  adConceptName: string;
  status: "idle" | "generating" | "completed" | "failed";
  imageUrl: string | null;
  error: string | null;
}

export interface CreativeBriefsState {
  status: "empty" | "generating" | "paused" | "generated" | "stale";
  generatedAt: string | null;
  queue: BriefJob[];
  completedCount: number;
  failedCount: number;
  totalCount: number;
  currentJobId: string | null;
  error: string | null;
  stageImageJobs?: Record<string, StageImageJob[]>;
  // Per-stage diversity mix returned by the strategist AI. Keyed by funnel
  // stage name (e.g. "Awareness"). Persisted so users don't have to
  // regenerate after navigating away.
  stageAiResults?: Record<string, any>;
  // Per-stage angle assignment results. Same key shape as stageAiResults.
  stageAngleResults?: Record<string, any>;
  // Per-stage "this stage has been saved" flag. Drives the saved badge
  // and gates downstream actions.
  savedStages?: Record<string, boolean>;
}

export function createEmptyCreativeBriefsState(): CreativeBriefsState {
  return {
    status: "empty",
    generatedAt: null,
    queue: [],
    completedCount: 0,
    failedCount: 0,
    totalCount: 0,
    currentJobId: null,
    error: null,
    stageImageJobs: {},
    stageAiResults: {},
    stageAngleResults: {},
    savedStages: {},
  };
}

export type WizardStepId =
  | "product"
  | "persona"
  | "narrativeOpportunity"
  | "narrativeAngles"
  | "funnelStrategy"
  | "mediaMix"
  | "adUnitSelections"
  | "campaignPlan"
  | "creativeBriefs";

export const WIZARD_STEPS: { id: WizardStepId; label: string; number: number }[] = [
  { id: "product", label: "Select Product", number: 1 },
  { id: "persona", label: "Select Persona", number: 2 },
  { id: "narrativeOpportunity", label: "Narrative Opportunity", number: 3 },
  { id: "narrativeAngles", label: "Narrative Angles", number: 4 },
  { id: "funnelStrategy", label: "Funnel Strategy", number: 5 },
  { id: "adUnitSelections", label: "Ad Unit Selection", number: 6 },
  { id: "campaignPlan", label: "Campaign Plan", number: 7 },
  { id: "creativeBriefs", label: "Creative Briefs", number: 8 },
];

export interface StalenessState {
  product: boolean;
  persona: boolean;
  narrativeOpportunity: boolean;
  narrativeAngles: boolean;
  funnelStrategy: boolean;
  mediaMix: boolean;
  adUnitSelections: boolean;
  campaignPlan: boolean;
  creativeBriefs: boolean;
}

export interface PlannerPersonaDecisionFramework {
  primary_focus: string;
  secondary_focus: string;
  what_this_planner_optimizes_for: string;
  what_this_planner_avoids: string;
}

export interface PlannerPersonaPersuasionStyle {
  tone: string;
  narrative_preference: string;
  emotional_vs_logical_bias: string;
  risk_tolerance: string;
}

export interface PlannerPersonaCreativePhilosophy {
  approach_to_creative_diversity: string;
  approach_to_scroll_stopping: string;
  approach_to_message_clarity: string;
}

export interface PlannerPersonaPlanningMethodology {
  campaign_structuring_style: string;
  testing_philosophy: string;
  how_the_planner_thinks_about_funnel_stages: string;
}

export interface PlannerPersonaAngleEvaluation {
  how_angles_are_compared: string;
  what_makes_an_angle_promising: string;
  what_makes_an_angle_weak: string;
}

export interface PlannerPersona {
  name: string;
  archetype: string;
  mission: string;
  strategic_mindset: string;
  core_beliefs_about_advertising: string[];
  decision_framework: PlannerPersonaDecisionFramework;
  persuasion_style: PlannerPersonaPersuasionStyle;
  angle_evaluation_strategy: PlannerPersonaAngleEvaluation;
  creative_philosophy: PlannerPersonaCreativePhilosophy;
  planning_methodology: PlannerPersonaPlanningMethodology;
  personality_traits: string[];
  signature_strength: string;
  blindspots_to_watch: string;
}

export interface CreativeStrategistCreativePhilosophy {
  what_good_creative_does: string;
  how_this_strategist_thinks_about_differentiation: string;
  how_this_strategist_thinks_about_funnel_fit: string;
}

export interface CreativeStrategistDecisionFramework {
  primary_focus: string;
  what_this_strategist_optimizes_for: string;
  what_this_strategist_avoids: string;
}

export interface CreativeStrategistAngleInterpretation {
  what_makes_an_angle_strong: string;
  how_angles_should_be_translated_into_creative: string;
  how_to_keep_angles_distinct: string;
}

export interface CreativeStrategistAudienceAndFunnelInstincts {
  audience_psychology: string;
  top_of_funnel: string;
  middle_of_funnel: string;
  bottom_of_funnel: string;
  skepticism_handling: string;
}

export interface CreativeStrategistCreativePreferences {
  preferred_hooks: string[];
  preferred_ad_approaches: string[];
  preferred_emotional_triggers: string[];
  preferred_proof_mechanisms: string[];
}

export interface CreativeStrategistEvaluationCriteria {
  how_this_strategist_judges_a_direction: string;
  signs_a_direction_has_potential: string[];
  signs_a_direction_is_generic: string[];
}

export interface CreativeStrategistWorkingStyle {
  planning_style: string;
  variation_strategy: string;
  how_overlap_is_prevented: string;
}

export interface CreativeStrategistSystemPromptIdentity {
  role_definition: string;
  operating_principles: string[];
  creative_non_negotiables: string[];
}

export interface CreativeStrategistPersona {
  name: string;
  archetype: string;
  mission: string;
  strategic_mindset: string;
  signature_strength: string;
  personality_traits: string[];
  core_beliefs_about_creative_strategy: string[];
  creative_philosophy: CreativeStrategistCreativePhilosophy;
  creative_preferences: CreativeStrategistCreativePreferences;
  decision_framework: CreativeStrategistDecisionFramework;
  audience_and_funnel_instincts: CreativeStrategistAudienceAndFunnelInstincts;
  angle_interpretation: CreativeStrategistAngleInterpretation;
  evaluation_criteria: CreativeStrategistEvaluationCriteria;
  working_style: CreativeStrategistWorkingStyle;
  system_prompt_identity: CreativeStrategistSystemPromptIdentity;
}

export interface MediaAllocationAdTypes {
  carousel: number;
  static: number;
  video: number;
}

export interface MediaAllocationStage {
  ad_types: MediaAllocationAdTypes;
  justification: string;
}

export interface MediaAllocationPlan {
  awareness: MediaAllocationStage;
  interest: MediaAllocationStage;
  consideration: MediaAllocationStage;
  intent: MediaAllocationStage;
  evaluation: MediaAllocationStage;
  purchase: MediaAllocationStage;
}

export interface MediaAllocationExecutiveSummary {
  summary: string;
  key_points: string[];
}

export interface MediaAllocation {
  allocation_plan: MediaAllocationPlan;
  executive_summary: MediaAllocationExecutiveSummary;
}

export interface CampaignDraft {
  id: string;
  name: string;
  product: Product | null;
  // Task #744 — chosen product variant name (null = "no specific variant").
  selectedVariantName: string | null;
  persona: Persona | null;
  narrativeOpportunity: NarrativeOpportunity | null;
  narrativeAngles: NarrativeAngle[];
  funnelStrategy: FunnelStrategyData | null;
  mediaMix: MediaMixConfig | null;
  adUnitSelections: AdUnitSelection[];
  adUnitPlan: AdUnitPlan | null;
  plannerPersona: PlannerPersona | null;
  creativeStrategist: CreativeStrategistPersona | null;
  mediaAllocation: MediaAllocation | null;
  campaignPlan: CampaignPlanSummary | null;
  creativeBriefs: CreativeBriefsState;
  staleness: StalenessState;
  currentStep: WizardStepId;
  completedSteps: WizardStepId[];
}

export function createEmptyDraft(): CampaignDraft {
  return {
    id: `draft-${Date.now()}`,
    name: "Untitled Campaign",
    product: null,
    selectedVariantName: null,
    persona: null,
    narrativeOpportunity: null,
    narrativeAngles: [],
    funnelStrategy: null,
    mediaMix: null,
    adUnitSelections: [],
    adUnitPlan: null,
    plannerPersona: null,
    creativeStrategist: null,
    mediaAllocation: null,
    campaignPlan: null,
    creativeBriefs: createEmptyCreativeBriefsState(),
    staleness: {
      product: false,
      persona: false,
      narrativeOpportunity: false,
      narrativeAngles: false,
      funnelStrategy: false,
      mediaMix: false,
      adUnitSelections: false,
      campaignPlan: false,
      creativeBriefs: false,
    },
    currentStep: "product",
    completedSteps: [],
  };
}


const STALENESS_DEPENDENCIES: Record<WizardStepId, WizardStepId[]> = {
  product: ["narrativeOpportunity", "narrativeAngles", "campaignPlan", "creativeBriefs"],
  persona: ["narrativeOpportunity", "narrativeAngles", "funnelStrategy", "campaignPlan", "creativeBriefs"],
  narrativeOpportunity: ["narrativeAngles", "campaignPlan", "creativeBriefs"],
  narrativeAngles: ["funnelStrategy", "campaignPlan", "creativeBriefs"],
  funnelStrategy: ["adUnitSelections", "campaignPlan", "creativeBriefs"],
  // Media mix (the "Total Static Ad Units" count) is now edited inside the
  // ad-unit-selection step itself, so changing the count there shouldn't
  // mark that same step stale — there's no upstream review to do. It still
  // invalidates downstream steps that bake in the count (campaign plan,
  // creative briefs).
  mediaMix: ["campaignPlan", "creativeBriefs"],
  adUnitSelections: ["campaignPlan", "creativeBriefs"],
  campaignPlan: ["creativeBriefs"],
  creativeBriefs: [],
};

export function markDownstreamStale(
  staleness: StalenessState,
  changedStep: WizardStepId
): StalenessState {
  const dependents = STALENESS_DEPENDENCIES[changedStep] ?? [];
  const updated = { ...staleness };
  for (const step of dependents) {
    updated[step] = true;
  }
  return updated;
}

export function isStepComplete(draft: CampaignDraft, stepId: WizardStepId): boolean {
  switch (stepId) {
    case "product":
      return draft.product !== null;
    case "persona":
      return draft.persona !== null;
    case "narrativeOpportunity":
      return draft.narrativeOpportunity !== null;
    case "narrativeAngles":
      return draft.narrativeAngles.length > 0;
    case "funnelStrategy":
      return draft.funnelStrategy !== null && !Array.isArray(draft.funnelStrategy) && Array.isArray(draft.funnelStrategy.stages);
    case "mediaMix":
      return draft.mediaMix !== null;
    case "adUnitSelections":
      // Zero selections is a valid state. The user is opting to let the AI
      // pick every ad unit downstream (selections act as a pool/preference,
      // not a requirement). Always treat this step as complete so users can
      // advance to the campaign plan even when no formats are selected.
      return true;
    case "campaignPlan":
      return draft.campaignPlan !== null;
    case "creativeBriefs":
      return draft.creativeBriefs.completedCount > 0;
    default:
      return false;
  }
}

export function canAdvanceToStep(draft: CampaignDraft, targetStep: WizardStepId): boolean {
  const targetIndex = WIZARD_STEPS.findIndex((s) => s.id === targetStep);
  if (targetIndex === 0) return true;
  for (let i = 0; i < targetIndex; i++) {
    if (!isStepComplete(draft, WIZARD_STEPS[i].id)) return false;
  }
  return true;
}
